import { useEffect, useMemo, useRef, useState } from 'react'
import { ActionIcon, TextInput, Tooltip } from '@mantine/core'
import {
  IconChevronLeft,
  IconChevronRight,
  IconPencil,
  IconSearch,
  IconStar,
  IconStarFilled,
  IconX,
} from '@tabler/icons-react'
import type { FavoriteEntry } from '../useFavorites'
import { EditFavoriteModal } from './EditFavoriteModal'
import { CATEGORIES, SAMPLE_QUESTIONS } from '../sample-questions'
import { cn } from '@/utils/cn'

const ALL_FILTER = 'all'
const FAVORITE_FILTER = 'favorite'
const COLLAPSE_BREAKPOINT_QUERY = '(max-width: 1023px)'

type SidebarQuestion = {
  text: string
  categoryId?: string
}

type SidebarProps = {
  favorites: FavoriteEntry[]
  favoriteTexts: Set<string>
  favoritesLoaded?: boolean
  onSelectQuestion: (text: string) => void
  onToggleFavorite: (text: string) => void
  onUpdateFavorite: (originalText: string, text: string, name?: string) => void
  isFavorite: (text: string) => boolean
}

export const Sidebar = ({
  favorites,
  favoriteTexts,
  favoritesLoaded = true,
  onSelectQuestion,
  onToggleFavorite,
  onUpdateFavorite,
  isFavorite,
}: SidebarProps) => {
  const [activeFilter, setActiveFilter] = useState<string>(ALL_FILTER)
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState(() => shouldStartCollapsed())
  const [editingFavorite, setEditingFavorite] = useState<FavoriteEntry | null>(null)
  const sampleByText = useMemo<ReadonlyMap<string, SidebarQuestion>>(
    () => new Map(SAMPLE_QUESTIONS.map((question) => [question.text, question])),
    [],
  )
  const favoritesByText = useMemo<ReadonlyMap<string, FavoriteEntry>>(
    () => new Map(favorites.map((favorite) => [favorite.text, favorite])),
    [favorites],
  )
  const initialLoadedSyncRef = useRef(favoritesLoaded)
  const [orderedQuestions, setOrderedQuestions] = useState<SidebarQuestion[]>(() =>
    buildQuestionsForFilter(ALL_FILTER, favorites, favoriteTexts, sampleByText),
  )

  useEffect(() => {
    if (!favoritesLoaded || initialLoadedSyncRef.current) return
    initialLoadedSyncRef.current = true
    setOrderedQuestions(
      buildQuestionsForFilter(activeFilter, favorites, favoriteTexts, sampleByText),
    )
  }, [activeFilter, favoriteTexts, favorites, favoritesLoaded, sampleByText])

  useEffect(() => {
    if (!favoritesLoaded) return
    setOrderedQuestions((prev) =>
      reconcileQuestionsForFilter(prev, activeFilter, favorites, favoriteTexts, sampleByText),
    )
  }, [activeFilter, favoriteTexts, favorites, favoritesLoaded, sampleByText])

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter)
    setOrderedQuestions(buildQuestionsForFilter(filter, favorites, favoriteTexts, sampleByText))
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (term.length === 0) return orderedQuestions
    return orderedQuestions.filter((question) => {
      if (question.text.toLowerCase().includes(term)) return true
      const favoriteName = favoritesByText.get(question.text)?.name
      return favoriteName ? favoriteName.toLowerCase().includes(term) : false
    })
  }, [orderedQuestions, search, favoritesByText])

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-[#e2e8f0] bg-white transition-[width] duration-200',
        collapsed ? 'w-[49px]' : 'w-[280px]',
      )}
    >
      <div
        className={cn(
          'flex h-[65px] items-center border-b border-[#e2e8f0] px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        <Tooltip label={collapsed ? 'Show questions' : 'Hide questions'} withArrow>
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#e2e8f0] bg-white text-slate-500 transition hover:border-[#bfdbfe] hover:bg-[#eff6ff] hover:text-[#2563eb]"
            aria-label={collapsed ? 'Show questions' : 'Hide questions'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
          </button>
        </Tooltip>
        {!collapsed && (
          <TextInput
            size="xs"
            className="ml-2 w-full"
            placeholder="Search questions..."
            leftSection={<IconSearch size={13} />}
            rightSection={
              search ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  radius="xl"
                  aria-label="Clear search"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setSearch('')}
                >
                  <IconX size={12} />
                </ActionIcon>
              ) : null
            }
            rightSectionPointerEvents={search ? 'all' : 'none'}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        )}
      </div>

      {!collapsed && (
        <>
          <div className="flex flex-wrap gap-1 border-b border-[#e2e8f0] px-3 py-3">
            <FilterChip
              id={ALL_FILTER}
              label="All"
              activeId={activeFilter}
              onClick={handleFilterClick}
            />
            <FilterChip
              id={FAVORITE_FILTER}
              label={`Favorite (${favorites.length})`}
              emoji="⭐"
              activeId={activeFilter}
              onClick={handleFilterClick}
            />
            {CATEGORIES.map((cat) => (
              <FilterChip
                key={cat.id}
                id={cat.id}
                label={cat.label}
                emoji={cat.emoji}
                activeId={activeFilter}
                onClick={handleFilterClick}
              />
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {filtered.length === 0 ? (
              <p className="px-2 py-4 text-center text-xs text-slate-400">No matching questions.</p>
            ) : (
              filtered.map((question) => {
                const favorite = isFavorite(question.text)
                const favoriteEntry = favoritesByText.get(question.text)
                const label = favoriteEntry?.name ?? question.text
                return (
                  <div
                    key={question.text}
                    className="group flex items-center gap-1 rounded-md transition hover:bg-[#eff6ff]"
                  >
                    <button
                      type="button"
                      onClick={() => onSelectQuestion(question.text)}
                      className={cn(
                        'line-clamp-2 flex-1 px-2 py-1.5 text-left text-xs leading-snug transition hover:text-[#2563eb]',
                        favoriteEntry?.name ? 'text-primary' : 'text-slate-700',
                      )}
                    >
                      {label}
                    </button>
                    {favoriteEntry ? (
                      <Tooltip label="Edit bookmark" withArrow>
                        <button
                          type="button"
                          onClick={() => setEditingFavorite(favoriteEntry)}
                          className="rounded p-1 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 hover:text-[#2563eb] focus-visible:opacity-100"
                          aria-label="Edit bookmark"
                        >
                          <IconPencil size={13} />
                        </button>
                      </Tooltip>
                    ) : null}
                    <Tooltip label={favorite ? 'Remove favorite' : 'Add favorite'} withArrow>
                      <button
                        type="button"
                        onClick={() => onToggleFavorite(question.text)}
                        className={cn(
                          'mr-1 rounded p-1 text-slate-300 hover:text-amber-500',
                          favorite && 'text-amber-500',
                        )}
                        aria-label={favorite ? 'Remove favorite' : 'Add favorite'}
                      >
                        {favorite ? <IconStarFilled size={13} /> : <IconStar size={13} />}
                      </button>
                    </Tooltip>
                  </div>
                )
              })
            )}
          </div>
        </>
      )}

      <EditFavoriteModal
        opened={editingFavorite !== null}
        favorite={editingFavorite}
        onClose={() => setEditingFavorite(null)}
        onSave={onUpdateFavorite}
      />
    </aside>
  )
}

function buildQuestionsForFilter(
  filter: string,
  favorites: FavoriteEntry[],
  favoriteTexts: Set<string>,
  sampleByText: ReadonlyMap<string, SidebarQuestion>,
): SidebarQuestion[] {
  const favoriteQuestions = favorites.map((favorite) => ({
    text: favorite.text,
    categoryId: sampleByText.get(favorite.text)?.categoryId,
  }))
  const sampleQuestions = SAMPLE_QUESTIONS.map((question) => ({
    text: question.text,
    categoryId: question.categoryId,
  }))

  if (filter === ALL_FILTER) {
    return [
      ...favoriteQuestions,
      ...sampleQuestions.filter((question) => !favoriteTexts.has(question.text)),
    ]
  }

  if (filter === FAVORITE_FILTER) {
    return favoriteQuestions
  }

  return sampleQuestions.filter((question) => question.categoryId === filter)
}

function reconcileQuestionsForFilter(
  current: SidebarQuestion[],
  filter: string,
  favorites: FavoriteEntry[],
  favoriteTexts: Set<string>,
  sampleByText: ReadonlyMap<string, SidebarQuestion>,
): SidebarQuestion[] {
  const latest = buildQuestionsForFilter(filter, favorites, favoriteTexts, sampleByText)
  const latestByText = new Map(latest.map((question) => [question.text, question]))
  const kept = current
    .filter((question) => latestByText.has(question.text))
    .map((question) => latestByText.get(question.text) ?? question)
  const keptTexts = new Set(kept.map((question) => question.text))
  const added = latest.filter((question) => !keptTexts.has(question.text))
  if (filter === FAVORITE_FILTER) {
    return [...added, ...kept]
  }
  if (filter === ALL_FILTER) {
    const addedFavorites = added.filter((question) => favoriteTexts.has(question.text))
    const otherAdded = added.filter((question) => !favoriteTexts.has(question.text))
    return [...addedFavorites, ...kept, ...otherAdded]
  }
  return [...kept, ...added]
}

type FilterChipProps = {
  id: string
  label: string
  emoji?: string
  activeId: string
  onClick: (id: string) => void
}

const FilterChip = ({ id, label, emoji, activeId, onClick }: FilterChipProps) => {
  const isActive = id === activeId
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={cn(
        'rounded-full border px-1.5 py-0.5 text-[9px] leading-tight font-semibold transition',
        isActive
          ? 'bg-primary border-primary text-white'
          : 'border-[#e2e8f0] bg-[#f8fafc] text-slate-600 hover:border-[#bfdbfe] hover:bg-[#eff6ff] hover:text-[#2563eb]',
      )}
    >
      {emoji ? <span className="mr-1">{emoji}</span> : null}
      {label}
    </button>
  )
}

function shouldStartCollapsed(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(COLLAPSE_BREAKPOINT_QUERY).matches
}
